import { useEffect, useId, useMemo, useState } from 'react';
import {
  HabitaEmptyState,
  HabitaErrorNotice,
  HabitaSelect,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import {
  GOAL_TYPE_DEFINITIONS,
  type GoalType,
  type GoalsCompetencyConfiguration,
  type CommercialTeamGoalProgressResponse,
} from '../../../shared/models';
import { formatMonthLabel } from '../../../shared/utils/monthOptions';
import { calculateGoalPace, GoalPaceBadge } from './GoalPaceBadge';

const goalCopy: Record<GoalType, {
  singular: string;
  title: string;
  unit: string;
}> = {
  PASTAS_APROVADAS: {
    singular: 'pasta',
    title: 'Meta de Pastas Aprovadas',
    unit: 'pastas aprovadas',
  },
  PASTAS_GERADAS: {
    singular: 'pasta',
    title: 'Meta de Pastas Geradas',
    unit: 'pastas geradas',
  },
  VISITAS_AGENDADAS: {
    singular: 'visita',
    title: 'Meta de Visitas Agendadas',
    unit: 'visitas',
  },
};

function formatNumber(value: number): string {
  return value.toLocaleString('pt-BR');
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })}%`;
}

function formatSignedPercent(value: number | null): string {
  if (value === null) return '—';
  const signal = value > 0 ? '+' : '';
  return `${signal}${formatPercent(value)}`;
}

function Gauge({
  actual,
  percent,
  target,
}: {
  actual: number;
  percent: number;
  target: number;
}) {
  const gradientId = useId().replace(/:/g, '');
  const clampedPercent = Math.min(100, Math.max(0, percent));
  const angle = Math.PI - (clampedPercent / 100) * Math.PI;
  const markerStart = {
    x: 120 + Math.cos(angle) * 82,
    y: 124 - Math.sin(angle) * 82,
  };
  const markerEnd = {
    x: 120 + Math.cos(angle) * 112,
    y: 124 - Math.sin(angle) * 112,
  };

  return (
    <div className="relative mx-auto w-full max-w-[280px]" aria-label={`${formatPercent(percent)} da meta`}>
      <div className="relative">
        <svg
          aria-hidden
          className="block h-auto w-full overflow-visible"
          viewBox="0 0 240 148"
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" x2="100%" y1="0%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="52%" stopColor="#facc15" />
              <stop offset="76%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#ef4444" />
            </linearGradient>
          </defs>
          <path
            d="M 22 124 A 98 98 0 0 1 218 124"
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeLinecap="round"
            strokeWidth="18"
          />
          <line
            x1={markerStart.x}
            x2={markerEnd.x}
            y1={markerStart.y}
            y2={markerEnd.y}
            className="stroke-textPrimary"
            strokeLinecap="round"
            strokeWidth="4"
          />
        </svg>

        <div className="pointer-events-none absolute inset-x-0 top-[58%] -translate-y-1/2 text-center">
          <p className="font-instrument text-4xl font-bold leading-none text-textPrimary">
            {formatNumber(actual)}
          </p>
          <p className="mt-1.5 font-instrument text-xs text-textSecondary">
            {formatPercent(percent)} da meta
          </p>
        </div>
      </div>

      <div className="mt-1 flex justify-between px-2 font-instrument text-xs font-medium text-textSecondary">
        <span>0</span>
        <span>{formatNumber(target)}</span>
      </div>
    </div>
  );
}

export default function TeamGoalGaugeCard({
  configuration,
  errorMessage,
  isLoading,
  progress,
}: {
  configuration: GoalsCompetencyConfiguration | null;
  errorMessage: string;
  isLoading: boolean;
  progress: CommercialTeamGoalProgressResponse | null;
}) {
  const enabledTypes = useMemo(() => (
    configuration?.goal_types
      .filter((item) => item.enabled)
      .map((item) => item.type) ?? []
  ), [configuration]);
  const [selectedType, setSelectedType] = useState<GoalType>('VISITAS_AGENDADAS');

  useEffect(() => {
    if (enabledTypes.includes(selectedType)) return;
    setSelectedType(
      enabledTypes.includes('VISITAS_AGENDADAS')
        ? 'VISITAS_AGENDADAS'
        : enabledTypes[0] ?? 'VISITAS_AGENDADAS'
    );
  }, [enabledTypes, selectedType]);

  const selectedGoal = configuration?.team_goals.find((goal) => goal.type === selectedType);
  const target = selectedGoal?.goal ?? 0;
  const actual = progress?.actuals[selectedType] ?? 0;
  const pace = configuration && selectedGoal
    ? calculateGoalPace({
      actual,
      referenceMonth: configuration.competency.period,
      target,
      totalWorkingDays: configuration.competency.working_days,
    })
    : null;
  const copy = goalCopy[selectedType];
  const hasConfiguredGoal = Boolean(
    selectedGoal
    && target > 0
    && enabledTypes.includes(selectedType)
  );

  return (
    <section className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      <header className="flex flex-col gap-3 border-b border-border-subtle px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h2 className="font-fraunces text-lg font-semibold text-textPrimary">
            {copy.title}
          </h2>
          {enabledTypes.length > 0 && (
            <HabitaSelect
              aria-label="Indicador da meta do time"
              className="h-9 min-w-[190px]"
              disabled={isLoading}
              value={selectedType}
              onChange={(event) => setSelectedType(event.target.value as GoalType)}
              options={enabledTypes.map((type) => ({
                label: GOAL_TYPE_DEFINITIONS.find((definition) => definition.type === type)?.label ?? type,
                value: type,
              }))}
            />
          )}
        </div>
        <p className="font-instrument text-xs font-medium text-textSecondary">
          Meta mensal · {configuration ? formatMonthLabel(configuration.competency.period) : 'competência atual'}
        </p>
      </header>

      {isLoading ? (
        <div className="grid min-h-[260px] animate-pulse gap-6 p-5 md:grid-cols-[280px_minmax(0,1fr)] md:items-center">
          <div className="mx-auto h-40 w-64 rounded-t-full bg-muted" />
          <div className="space-y-4">
            <div className="h-8 w-56 rounded bg-muted" />
            <div className="h-7 w-28 rounded-full bg-muted" />
            <div className="h-16 rounded-md bg-muted" />
            <div className="h-4 w-4/5 rounded bg-muted" />
          </div>
        </div>
      ) : errorMessage ? (
        <div className="p-5">
          <HabitaErrorNotice>{errorMessage}</HabitaErrorNotice>
        </div>
      ) : enabledTypes.length === 0 || !hasConfiguredGoal || !pace ? (
        <div className="p-5">
          <HabitaEmptyState
            className="border-0"
            icon="ri-speed-up-line"
            title="Meta não configurada para este indicador neste mês"
            description="Habilite o indicador e informe a Meta do Time em Parâmetros para acompanhar o ritmo."
          />
        </div>
      ) : (
        <div className="grid gap-7 p-5 md:grid-cols-[280px_minmax(0,1fr)] md:items-center lg:gap-10 lg:px-8">
          <Gauge actual={actual} percent={pace.actualPercent} target={target} />

          <div className="min-w-0 space-y-4">
            <div>
              <p className="font-instrument text-2xl font-bold text-textPrimary">
                {formatNumber(actual)}
                <span className="ml-1.5 text-sm font-medium text-textSecondary">
                  de {formatNumber(target)} {copy.unit}
                </span>
              </p>
              <div className="mt-3">
                <GoalPaceBadge pace={pace} />
              </div>
            </div>

            <div className="rounded-md border border-border-subtle bg-muted/35 px-4 py-3">
              <p className="font-instrument text-[10px] font-semibold uppercase text-textSecondary">
                Faltam para a meta
              </p>
              <p className="mt-1 font-instrument text-base font-semibold text-textPrimary">
                {formatNumber(pace.remaining)} {pace.remaining === 1 ? copy.singular : copy.unit}
              </p>
            </div>

            <p className="font-instrument text-xs leading-relaxed text-textSecondary">
              Ritmo esperado até hoje (dia{' '}
              <strong className="text-textPrimary">
                {pace.elapsedWorkingDays}/{pace.totalWorkingDays}
              </strong>
              ): <strong className="text-textPrimary">{formatNumber(pace.expectedValue)} {copy.unit}</strong>
              {' '}({formatPercent(pace.expectedPercent)} do período) · diferença vs. esperado:{' '}
              <strong className={classNames(
                pace.differenceFromExpectedPct !== null && pace.differenceFromExpectedPct >= 0
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-red-700 dark:text-red-300'
              )}>
                {formatSignedPercent(pace.differenceFromExpectedPct)}
              </strong>.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
